// AssetManifest — central registry mapping logical asset IDs to file paths.
//
// The pattern: every art helper (PortraitArt, UnitArt, etc.) consults the
// manifest first. If a real asset file is registered for the requested ID,
// we load and use it. Otherwise we fall back to procedural canvas generation.
//
// This lets us drop in real assets one-by-one (e.g., the Amar portrait first,
// then Lucian, then a swordsman sprite sheet) without breaking anything that
// hasn't been replaced yet. The procedural code stays as a permanent fallback.
//
// All paths are relative to /public, served at /<path>.

import { BESPOKE_SPRITES, type ClassKind } from "../combat/types";
import { DEFAULT_VARIANT_FOR, PORTRAIT_EXPRESSIONS } from "./expressions";
import { ASSET_VERSIONS } from "./assetVersions.gen";

// Streamed assets live at STABLE urls with a day of browser cache
// (vercel.json) — so a re-dropped file kept serving stale art from
// returning players' caches for up to a day. Every loader call site
// routes its url through here: the content hash (generated by
// scripts/hashAssets.mjs at build time) rides along as ?v=, so changed
// bytes mean a changed url (instant refresh) while unchanged files keep
// their exact old url and stay cache-warm. Unhashed paths (a registered
// file that doesn't exist yet) pass through bare and 404 harmlessly,
// same as before.
export const versionedPath = (path: string): string => {
  const v = ASSET_VERSIONS[path];
  return v ? `${path}?v=${v}` : path;
};

// --------- Asset specs (sizes, frame counts) ---------------------------------
//
// These dimensions match what the existing procedural code already produces,
// so we don't have to refactor combat. To upgrade fidelity later, bump these
// constants AND every reference site at the same time.

export const ASSET_SPEC = {
  portrait: { w: 64, h: 72 },
  unitSprite: { w: 32, h: 40 }, // single static frame
  // Animation strips. The frame count is "what the spritesheet should contain";
  // BootScene loads them as a Phaser spritesheet using these dimensions.
  unitAnim: {
    w: 32,
    h: 40,
    frames: {
      idle: 2,    // 2-frame breathing loop ~1fps
      walk: 4,    // 4-frame walk cycle
      attack: 5,  // wind-up + swing + recover
      hit: 2,     // flinch + recover
      death: 4    // fall + fade
    } as const
  },
  tile: { w: 48, h: 48 },
  backdrop: { w: 1280, h: 720 },
  // VFX overlays — slash arcs, hit sparks, arrow trails.
  vfx: {
    slash: { w: 64, h: 64, frames: 5 },
    hitSpark: { w: 32, h: 32, frames: 4 },
    arrow: { w: 24, h: 8, frames: 1 }
  }
} as const;

export type UnitAnimState = keyof typeof ASSET_SPEC.unitAnim.frames;

// --------- Manifest entries --------------------------------------------------
//
// To register a new real asset, add an entry here. BootScene reads this list
// at startup and queues each one for loading.
//
// Each entry has:
//   id     — the logical key the game code uses (e.g., "portrait:amar")
//   path   — file path under /public
//   kind   — "image" | "spritesheet" | "audio"
//   frame  — for spritesheets, frame dimensions

export type AssetKind = "image" | "spritesheet" | "audio";

export interface ManifestEntry {
  id: string;
  path: string;
  kind: AssetKind;
  frame?: { w: number; h: number };
}

// All known portraits. Add a file at public/assets/portraits/<id>.png and
// register the id here to load it on boot. The side panel and dialogue
// system look up portraits by `portrait:<id>`; missing files silently
// fall through to the procedural portrait painter.
//
// Generic enemy portraits (bandit / raider / reaver / royal_guard /
// crown_archer) are shared across every instance of that enemy class —
// the ENEMIES factory in src/data/units.ts sets `portraitId: "bandit"`
// (etc.) so dawn_sw1, amb_sw1, nd_b1, etc. all route to the same artwork.
const PORTRAIT_IDS = [
  // Named characters
  "amar", "lucian", "ning", "maya", "leo", "ranatoli", "selene",
  "veya", "corin",
  "kian", "ndari", "nebu",
  "dawn", "fergus", "ndara", "archbold", "khione", "mira", "tali",
  "rose", "coyne",
  // The named generals, and the Ravage's Herald and Commander.
  "castor", "othren", "wren", "serrick", "brask", "vasse", "sarto",
  "herald", "ravage_commander",
  "narrator",
  // Generic enemy classes
  "bandit", "raider", "reaver", "royal_guard", "crown_archer"
] as const;

const baseEntries: ManifestEntry[] = PORTRAIT_IDS.map((id) => {
  // For characters with a curated default variant, prefer it over the legacy
  // base file. The variant slug usually resolves to `<id>_neutral.png`, but
  // it can be any expression file (e.g., maya → "guarded_neutral").
  const variant = DEFAULT_VARIANT_FOR.get(id);
  return {
    id: `portrait:${id}`,
    path: variant
      ? `assets/portraits/${id}_${variant}.webp`
      : `assets/portraits/${id}.webp`,
    kind: "image" as const
  };
});

// Expression variants — `<character>_<expression>.png`. Loaded as
// `portrait:<id>:<expression>`. Missing files silently 404 and the dialog
// falls back to the default portrait.
const expressionEntries: ManifestEntry[] = Object.entries(PORTRAIT_EXPRESSIONS).flatMap(
  ([id, exprs]) => exprs.map((expr) => ({
    id: `portrait:${id}:${expr}`,
    path: `assets/portraits/${id}_${expr}.webp`,
    kind: "image" as const
  }))
);

const portraitEntries: ManifestEntry[] = [...baseEntries, ...expressionEntries];

// Per-class animation strips. We register the full set per class; if a file
// doesn't exist on disk Vite's loader will simply 404 and Phaser will skip it
// (we suppress the load error and fall back to procedural).
const CLASSES: ClassKind[] = [
  "swordsman", "spearton", "knight", "archer",
  "shinobi", "sentinel", "dactyl_rider", "swordmaster", "boss",
  // The lenscaster, then the Tier 2 (promotion) classes. resolveSpriteClass
  // upgrades a unit to its class's art whenever that class's idle loaded;
  // a missing folder 404s harmlessly and the unit keeps its stand-in.
  "lenscaster", "khan", "prismarch", "spearton_lord",
  "robinhelm", "dactyl_king", "shinobi_master", "guardian"
];

const ANIM_STATES: UnitAnimState[] = ["idle", "walk", "attack", "hit", "death"];

const unitAnimEntries: ManifestEntry[] = [...CLASSES, ...BESPOKE_SPRITES].flatMap((cls) =>
  ANIM_STATES.map((state) => ({
    id: `unit:${cls}:${state}`,
    path: `assets/sprites/${cls}/${state}.png`,
    kind: "spritesheet" as const,
    frame: { w: ASSET_SPEC.unitAnim.w, h: ASSET_SPEC.unitAnim.h }
  }))
);

// Backdrops keyed by the same names BackdropArt uses today. The first row is
// outdoor battle/world backdrops; the second row is interior/intimate scenes
// used by story arcs (the file basenames are snake_case as generated).
const BACKDROP_IDS = [
  "palaceCoup", "thuling", "farmland", "mountain", "swamp",
  "caravan", "monastery", "orinhal", "cliffs", "grude", "finalBoss",
  "factory", "field_night_camp", "rusty_house", "study", "tavern"
] as const;

const backdropEntries: ManifestEntry[] = BACKDROP_IDS.map((id) => ({
  id: `backdrop:${id}`,
  path: `assets/backdrops/${id}.webp`,
  kind: "image"
}));

// Tiles by terrain name. Optional override for the procedural tile painter.
// First row: the main 12 documented in public/assets/tiles/README.md.
// Second row: bonus environment tiles (ice/lava/etc.) — usable from any
// MapDef once the matching TerrainKind value is selected.
const TILE_IDS = [
  "grass", "stone", "wood", "marble", "dirt", "snow", "sand", "water",
  "forest", "wall", "door", "rubble",
  "cobblestone", "cracked_earth", "ice", "lava", "moss_stone", "mud"
] as const;

const tileEntries: ManifestEntry[] = TILE_IDS.map((id) => ({
  id: `tile:${id}`,
  path: `assets/tiles/${id}.webp`,
  kind: "image"
}));

// Obstacles — one PNG per ObstacleKind, drawn over the base tile in
// ensureTileTexture(). Missing files fall back to the procedural painter.
const OBSTACLE_IDS = [
  "hay", "fence", "wagon", "barricade", "pillar", "throne",
  "tree", "rock", "torch"
] as const;

const obstacleEntries: ManifestEntry[] = OBSTACLE_IDS.map((id) => ({
  id: `obstacle:${id}`,
  path: `assets/obstacles/${id}.png`,
  kind: "image"
}));

// Standing versions of the props the ¾ board stands up as billboards. The
// art above is painted for the flat board — a tree there is a canopy seen
// from above — so on the tilted board it read as a bush. Drawn at the unit
// sprites' pixel density (see Diorama's TALL_PROP_SCALE).
export const TALL_OBSTACLE_IDS = ["tree", "pillar"] as const;

const tallObstacleEntries: ManifestEntry[] = TALL_OBSTACLE_IDS.map((id) => ({
  id: `obstacle_tall:${id}`,
  path: `assets/obstacles/tall/${id}.png`,
  kind: "image"
}));

// VFX
const vfxEntries: ManifestEntry[] = [
  // Painted effects (gen_vfx_art.py): 128x128 frames on black, additive.
  { id: "vfx:paint_slash",      path: "assets/vfx/slash.png",      kind: "spritesheet", frame: { w: 128, h: 128 } },
  { id: "vfx:paint_slash_crit", path: "assets/vfx/slash_crit.png", kind: "spritesheet", frame: { w: 128, h: 128 } },
  { id: "vfx:paint_impact",     path: "assets/vfx/impact.png",     kind: "spritesheet", frame: { w: 128, h: 128 } },
  { id: "vfx:paint_heal",       path: "assets/vfx/heal.png",       kind: "spritesheet", frame: { w: 128, h: 128 } },
];
// (The old pixel slash / hit-spark / arrow entries pointed at files that
// never shipped; the painted strips above replaced them.)

// UI overrides. The procedural parchment panel is fine, but if you drop a
// real PNG here it'll be used instead.
const uiEntries: ManifestEntry[] = [
  { id: "ui:panel",        path: "assets/ui/panel.png",        kind: "image" },
  { id: "ui:button_idle",  path: "assets/ui/button_idle.png",  kind: "image" },
  { id: "ui:button_hover", path: "assets/ui/button_hover.png", kind: "image" }
];

// The camp diorama's art (public/assets/camp/), drawn by CampScene.
//
// Props are pixel art at the unit sheets' density — every art pixel is
// drawn at 2 world px, as the camp draws the 32x40 unit frames at 64x80 —
// and stand on the camp board as billboards, base on the ground. They are
// painted lit from the RIGHT (an off-picture fire), so CampScene mirrors
// any prop standing to the right of the camp's fire.
//
//   flames   — 8 frames of 34x50 in one row: the fire's loop, every frame
//              on one baseline and centred on its base.
//   firepit  — the stone ring and embers the flames stand in.
//   sky      — the painted night beyond the board (LINEAR: it is a
//              painting, not pixel art), drawn by CampBackdropScene.
//
// Sources and the generation/processing scripts: art_sources/camp/,
// scripts/art/gen_camp_art.py, scripts/art/process_camp_art.py.
const campEntries: ManifestEntry[] = [
  { id: "camp:flames", path: "assets/camp/flames.png", kind: "spritesheet", frame: { w: 34, h: 50 } },
  { id: "camp:firepit", path: "assets/camp/firepit.png", kind: "image" },
  { id: "camp:wagon", path: "assets/camp/wagon.png", kind: "image" },
  { id: "camp:tent", path: "assets/camp/tent.png", kind: "image" },
  { id: "camp:log", path: "assets/camp/log.png", kind: "image" },
  { id: "camp:crates", path: "assets/camp/crates.png", kind: "image" },
  { id: "camp:lantern", path: "assets/camp/lantern.png", kind: "image" },
  { id: "camp:pine", path: "assets/camp/pine.png", kind: "image" },
  { id: "camp:memorial", path: "assets/camp/memorial.png", kind: "image" },
  { id: "backdrop:camp_sky", path: "assets/camp/sky.webp", kind: "image" }
];

// The story's staged pictures (DialogBeat.tableau): the wedding codas' ring
// at sunset — a painted clifftop and the ring itself (scenes/story/
// RingTableau; scripts/art/gen_ring_art.py, process_ring_art.py).
const storyEntries: ManifestEntry[] = [
  { id: "backdrop:ring_cliff", path: "assets/story/ring_cliff.webp", kind: "image" },
  { id: "story:ring", path: "assets/story/ring.png", kind: "image" },
  // The story's cinematics and stages (scenes/story/Cinematics).
  { id: "backdrop:harbor_night", path: "assets/story/harbor_night.webp", kind: "image" },
  { id: "backdrop:open_sea", path: "assets/story/open_sea.webp", kind: "image" },
  { id: "story:ship", path: "assets/story/ship.webp", kind: "image" },
  { id: "story:ravage_ship", path: "assets/story/ravage_ship.webp", kind: "image" }
];

// The opening film plays the moment a new game starts: its throne hall
// streams in first (~200KB).
const openingEntries: ManifestEntry[] = [
  { id: "backdrop:throne_hall", path: "assets/story/throne_hall.webp", kind: "image" }
];

export const MANIFEST: ManifestEntry[] = [
  ...portraitEntries,
  ...unitAnimEntries,
  ...openingEntries,
  // Early in the stream: ~230KB in all, and the camp is often the first
  // page after the title.
  ...campEntries,
  ...backdropEntries,
  ...tileEntries,
  ...obstacleEntries,
  ...tallObstacleEntries,
  ...vfxEntries,
  ...uiEntries,
  ...storyEntries
];

// --------- Runtime check: is a given asset id loaded? ------------------------
//
// BootScene populates this set as files successfully load. Art helpers ask
// hasAsset(id) before deciding whether to use the real image or the
// procedural fallback.

const loaded = new Set<string>();

export const markLoaded = (id: string): void => { loaded.add(id); };
export const markFailed = (id: string): void => { loaded.delete(id); };
export const hasAsset = (id: string): boolean => loaded.has(id);
export const loadedAssetIds = (): readonly string[] => Array.from(loaded);
